import { useState, useEffect } from 'react';
import { Sparkles, Tag, FileText, ListMusic, Loader, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type AIFeature = 'recommendations' | 'tags' | 'description' | 'playlist';

interface AILog {
  id: string;
  feature_type: AIFeature;
  input_data: any;
  output_data: any;
  tokens_used: number;
  created_at: string;
}

export function AdminPanel() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<AILog[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<AIFeature | null>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [recommendInput, setRecommendInput] = useState({ userId: user?.id || '', limit: 10 });
  const [tagInput, setTagInput] = useState({ title: '', description: '', youtubeUrl: '' });
  const [descriptionInput, setDescriptionInput] = useState({ title: '', tags: '', locationName: '' });
  const [playlistInput, setPlaylistInput] = useState({
    latitude: 37.5665,
    longitude: 126.9780,
    radius: 5000,
    theme: '',
    mood: '',
    limit: 20
  });

  useEffect(() => {
    fetchLogs();
    if (user?.id) {
      setRecommendInput(prev => ({ ...prev, userId: user.id }));
    }
  }, [user]);

  const fetchLogs = async () => {
    const { data } = await supabase
      .from('ai_curation_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    if (data) setLogs(data);
  };

  const callAIFunction = async (feature: AIFeature, input: any) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const functionMap = {
        recommendations: 'ai-recommend-drops',
        tags: 'ai-generate-tags',
        description: 'ai-generate-description',
        playlist: 'ai-curate-playlist',
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionMap[feature]}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(input),
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      setResult(data);
      fetchLogs();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      id: 'recommendations' as AIFeature,
      icon: Sparkles,
      title: language === 'ko' ? 'AI 추천' : 'AI Recommendations',
      description: language === 'ko'
        ? '사용자 취향 기반 사운드 드롭 추천'
        : 'Personalized sound drop recommendations',
      color: 'from-purple-500 to-pink-500',
      howTo: language === 'ko'
        ? '사용자의 청취 이력을 분석해 맞춤형 음악을 추천합니다'
        : 'Analyzes listening history for personalized recommendations',
    },
    {
      id: 'tags' as AIFeature,
      icon: Tag,
      title: language === 'ko' ? '자동 태그' : 'Auto Tags',
      description: language === 'ko'
        ? '음악에 맞는 태그 자동 생성'
        : 'Generate relevant music tags automatically',
      color: 'from-blue-500 to-cyan-500',
      howTo: language === 'ko'
        ? '제목과 설명을 분석해 장르, 무드, 악기 태그를 생성합니다'
        : 'Analyzes title and description to generate genre, mood, and instrument tags',
    },
    {
      id: 'description' as AIFeature,
      icon: FileText,
      title: language === 'ko' ? '설명 생성' : 'Description',
      description: language === 'ko'
        ? '매력적인 음악 설명 자동 작성'
        : 'Create engaging music descriptions',
      color: 'from-green-500 to-emerald-500',
      howTo: language === 'ko'
        ? '음악 정보를 바탕으로 2-3줄의 매력적인 설명을 작성합니다'
        : 'Creates 2-3 sentence engaging descriptions based on music info',
    },
    {
      id: 'playlist' as AIFeature,
      icon: ListMusic,
      title: language === 'ko' ? '플레이리스트 큐레이션' : 'Playlist Curation',
      description: language === 'ko'
        ? '위치 기반 맞춤 플레이리스트'
        : 'Location-based curated playlists',
      color: 'from-orange-500 to-red-500',
      howTo: language === 'ko'
        ? '특정 지역의 음악을 분석해 테마에 맞는 플레이리스트를 만듭니다'
        : 'Analyzes local music to create themed playlists',
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-gray-900">
      <div className="max-w-7xl mx-auto p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {language === 'ko' ? 'AI 큐레이션 관리자' : 'AI Curation Admin'}
          </h1>
          <p className="text-gray-300">
            {language === 'ko'
              ? 'MapSound의 AI 기능을 관리하고 테스트하세요'
              : 'Manage and test AI-powered features for MapSound'}
          </p>
          <div className="mt-4 bg-teal-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#00c7b7] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              {language === 'ko'
                ? '아래 카드를 클릭하여 각 AI 기능을 테스트할 수 있습니다. 관리자는 무제한으로 사용 가능하며, 일반 사용자는 크레딧이 차감됩니다.'
                : 'Click the cards below to test each AI feature. Admins have unlimited usage, regular users consume credits.'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {features.map((feature) => (
            <button
              key={feature.id}
              onClick={() => {
                setSelectedFeature(feature.id);
                setResult(null);
                setError(null);
              }}
              className={`p-6 bg-gray-800 border border-gray-700 rounded-xl shadow-md hover:shadow-lg transition-all text-left ${
                selectedFeature === feature.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-300 mb-3">{feature.description}</p>
              <p className="text-xs text-gray-400">{feature.howTo}</p>
            </button>
          ))}
        </div>

        {selectedFeature === 'recommendations' && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold mb-2">
              {language === 'ko' ? 'AI 음악 추천' : 'AI Music Recommendations'}
            </h2>
            <p className="text-sm text-gray-300 mb-4">
              {language === 'ko'
                ? '사용자의 청취 패턴을 분석하여 맞춤형 음악을 추천합니다'
                : 'Analyzes user listening patterns to recommend personalized music'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '사용자 ID' : 'User ID'}
                </label>
                <input
                  type="text"
                  value={recommendInput.userId}
                  onChange={(e) => setRecommendInput({ ...recommendInput, userId: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '현재 로그인된 사용자' : 'Current logged in user'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '추천 개수' : 'Number of Recommendations'}
                </label>
                <input
                  type="number"
                  value={recommendInput.limit}
                  onChange={(e) => setRecommendInput({ ...recommendInput, limit: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  min="1"
                  max="50"
                />
              </div>
              <button
                onClick={() => callAIFunction('recommendations', recommendInput)}
                disabled={loading || !recommendInput.userId}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  language === 'ko' ? '추천 생성' : 'Generate Recommendations'
                )}
              </button>
            </div>
          </div>
        )}

        {selectedFeature === 'tags' && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold mb-2">
              {language === 'ko' ? '자동 태그 생성' : 'Auto-Tag Generation'}
            </h2>
            <p className="text-sm text-gray-300 mb-4">
              {language === 'ko'
                ? '음악 정보를 분석하여 관련 태그를 자동으로 생성합니다'
                : 'Analyzes music info to automatically generate relevant tags'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '제목 (필수)' : 'Title (Required)'}
                </label>
                <input
                  type="text"
                  value={tagInput.title}
                  onChange={(e) => setTagInput({ ...tagInput, title: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '예: 여름날의 재즈' : 'e.g., Summer Jazz Vibes'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '설명 (선택)' : 'Description (Optional)'}
                </label>
                <textarea
                  value={tagInput.description}
                  onChange={(e) => setTagInput({ ...tagInput, description: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  rows={3}
                  placeholder={language === 'ko' ? '간단한 설명을 입력하세요' : 'Brief description'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? 'YouTube URL (선택)' : 'YouTube URL (Optional)'}
                </label>
                <input
                  type="text"
                  value={tagInput.youtubeUrl}
                  onChange={(e) => setTagInput({ ...tagInput, youtubeUrl: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <button
                onClick={() => callAIFunction('tags', tagInput)}
                disabled={loading || !tagInput.title}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  language === 'ko' ? '태그 생성' : 'Generate Tags'
                )}
              </button>
            </div>
          </div>
        )}

        {selectedFeature === 'description' && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold mb-2">
              {language === 'ko' ? '설명 자동 생성' : 'Description Generator'}
            </h2>
            <p className="text-sm text-gray-300 mb-4">
              {language === 'ko'
                ? '음악에 대한 매력적인 설명을 자동으로 작성합니다'
                : 'Automatically creates engaging music descriptions'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '제목 (필수)' : 'Title (Required)'}
                </label>
                <input
                  type="text"
                  value={descriptionInput.title}
                  onChange={(e) => setDescriptionInput({ ...descriptionInput, title: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '음악 제목' : 'Music title'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '태그 (쉼표로 구분)' : 'Tags (comma-separated)'}
                </label>
                <input
                  type="text"
                  value={descriptionInput.tags}
                  onChange={(e) => setDescriptionInput({ ...descriptionInput, tags: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '재즈, 차분한, 피아노' : 'jazz, calm, piano'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '장소 이름' : 'Location Name'}
                </label>
                <input
                  type="text"
                  value={descriptionInput.locationName}
                  onChange={(e) => setDescriptionInput({ ...descriptionInput, locationName: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '예: 서울 홍대' : 'e.g., Hongdae, Seoul'}
                />
              </div>
              <button
                onClick={() => callAIFunction('description', {
                  ...descriptionInput,
                  tags: descriptionInput.tags.split(',').map(t => t.trim()).filter(Boolean),
                })}
                disabled={loading || !descriptionInput.title}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  language === 'ko' ? '설명 생성' : 'Generate Description'
                )}
              </button>
            </div>
          </div>
        )}

        {selectedFeature === 'playlist' && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-bold mb-2">
              {language === 'ko' ? '플레이리스트 큐레이션' : 'Playlist Curation'}
            </h2>
            <p className="text-sm text-gray-300 mb-4">
              {language === 'ko'
                ? '특정 지역의 음악을 분석하여 테마에 맞는 플레이리스트를 생성합니다'
                : 'Analyzes music in a specific area to create themed playlists'}
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {language === 'ko' ? '위도' : 'Latitude'}
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={playlistInput.latitude}
                    onChange={(e) => setPlaylistInput({ ...playlistInput, latitude: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {language === 'ko' ? '경도' : 'Longitude'}
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={playlistInput.longitude}
                    onChange={(e) => setPlaylistInput({ ...playlistInput, longitude: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '반경 (미터)' : 'Radius (meters)'}
                </label>
                <input
                  type="number"
                  value={playlistInput.radius}
                  onChange={(e) => setPlaylistInput({ ...playlistInput, radius: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  min="100"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {language === 'ko' ? '현재: ' : 'Current: '}{(playlistInput.radius / 1000).toFixed(1)}km
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '테마 (선택)' : 'Theme (Optional)'}
                </label>
                <input
                  type="text"
                  value={playlistInput.theme}
                  onChange={(e) => setPlaylistInput({ ...playlistInput, theme: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '예: 아침 산책, 야간 드라이브' : 'e.g., morning walk, night drive'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {language === 'ko' ? '분위기 (선택)' : 'Mood (Optional)'}
                </label>
                <input
                  type="text"
                  value={playlistInput.mood}
                  onChange={(e) => setPlaylistInput({ ...playlistInput, mood: e.target.value })}
                  className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder={language === 'ko' ? '예: 활기찬, 차분한, 신나는' : 'e.g., energetic, calm, upbeat'}
                />
              </div>
              <button
                onClick={() => callAIFunction('playlist', playlistInput)}
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  language === 'ko' ? '플레이리스트 생성' : 'Curate Playlist'
                )}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">
                {language === 'ko' ? '오류 발생' : 'Error'}
              </h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-md p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <h2 className="text-xl font-bold">
                {language === 'ko' ? '결과' : 'Result'}
              </h2>
            </div>
            <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm text-gray-800">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}

        <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">
            {language === 'ko' ? '최근 AI 활동 기록' : 'Recent AI Activity'}
          </h2>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Info className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'ko' ? '아직 기록이 없습니다' : 'No activity yet'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white capitalize">
                      {log.feature_type === 'recommendations' && (language === 'ko' ? '추천' : 'Recommendations')}
                      {log.feature_type === 'tags' && (language === 'ko' ? '태그' : 'Tags')}
                      {log.feature_type === 'description' && (language === 'ko' ? '설명' : 'Description')}
                      {log.feature_type === 'playlist' && (language === 'ko' ? '플레이리스트' : 'Playlist')}
                    </span>
                    <span className="text-sm text-gray-400">
                      {new Date(log.created_at).toLocaleString(language === 'ko' ? 'ko-KR' : 'en-US')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-300">
                    <div>
                      {language === 'ko' ? '사용 토큰:' : 'Tokens used:'} {log.tokens_used}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
